import { profileColumns } from './columnProfiler';
import { applyLayout, dateTimeLayoutsOf } from './layout';
import type { Layout } from './layout';
import type { ColumnOverride, ColumnProfile, ParsedDataPoint } from './types';
import type { PlausibilityFlag } from './plausibilityValidator';
import type { AMIParseResult, SheetContext } from './parser';

/**
 * The reading search: when the first reading of a file does not hold together,
 * try the file's other plausible readings and keep the one that does.
 *
 * Column detection makes one guess and commits to it. When the guess is wrong a
 * Layer 0 invariant refuses the file, and until 2026-10-21 nothing tried again:
 * the ComEd export that took END TIME as its timestamp was refused outright,
 * although another reading of the same file was sitting one column over. A wrong
 * reading almost never produces a real meter history by accident — readings at
 * one steady interval, about as many of them as the dates imply, every
 * invariant satisfied — so that is the test each trial must pass.
 *
 * Bounds, so a search cannot invent a reading:
 *   - Readings come only from a column whose header names energy or demand
 *     (a kW, kWh or kVA keyword, or a demand-tier word). A column of dollars is
 *     numeric and evenly spaced too; it is never a candidate.
 *   - Every trial runs the whole of readSheet: the same invariants, the same
 *     plausibility checks, the same refusals.
 *   - A first reading that loaded but looks uneven is kept, never swapped. A
 *     different peak from another reading is flagged for the preparer to check.
 *   - The search never runs on instructions someone gave (the preparer's columns,
 *     a saved layout). Those are followed, and refused if wrong.
 */

/** Share of gaps that must equal the commonest gap. Real exports run 0.97+. */
const MIN_REGULARITY = 0.8;
/** Readings ÷ the count the date range implies at that gap. */
const MIN_COVERAGE = 0.7;
/** Below this many readings the shape of the series says too little to judge. */
const MIN_POINTS_TO_JUDGE = 20;
const MAX_TRIALS = 12;
const DATE_RATIO = 0.7;
const NUMERIC_RATIO = 0.7;

type ReadSheet = (ctx: SheetContext, layout: Layout, override: ColumnOverride | undefined, source: 'preparer' | 'trial') => AMIParseResult;

export interface Coherence {
  holds: boolean;
  regularity: number;
  coverage: number;
  intervalMinutes: number | null;
}

/** Whether a set of readings looks like a meter history: one steady interval, few holes. */
export function coherenceOf(points: ParsedDataPoint[]): Coherence {
  const times = [...new Set(points.map(p => Date.parse(p.timestamp)))].filter(Number.isFinite).sort((a, b) => a - b);
  if (times.length < MIN_POINTS_TO_JUDGE) return { holds: times.length > 0, regularity: 1, coverage: 1, intervalMinutes: null };
  const tally = new Map<number, number>();
  for (let i = 1; i < times.length; i++) {
    const gap = Math.round((times[i] - times[i - 1]) / 60000);
    tally.set(gap, (tally.get(gap) ?? 0) + 1);
  }
  let interval = 0;
  let count = 0;
  for (const [gap, n] of tally) if (n > count) { interval = gap; count = n; }
  const regularity = count / (times.length - 1);
  const spanMinutes = (times[times.length - 1] - times[0]) / 60000;
  const coverage = interval > 0 ? Math.min(1, times.length / (spanMinutes / interval + 1)) : 0;
  const holds = interval >= 1 && interval <= 1440 && regularity >= MIN_REGULARITY && coverage >= MIN_COVERAGE;
  return { holds, regularity, coverage, intervalMinutes: interval || null };
}

/** A reading that loaded, with no errors, and holds together. */
export function readingHoldsTogether(result: AMIParseResult): boolean {
  return result.data.length > 0 && result.errors.length === 0 && coherenceOf(result.data).holds;
}

function labelled(p: ColumnProfile): boolean {
  const s = p.headerSignal;
  return !s.excluded && !s.isGeneration && (s.demandTier > 0 || s.isKwh || s.isKva);
}

/** The readings worth trying: layouts the sheet supports × date columns × labelled value columns. */
function trialsFor(ctx: SheetContext, detected: Layout): Array<{ layout: Layout; override: ColumnOverride }> {
  const { chosenSheet, headerRow, shape } = ctx;
  const layouts: Layout[] = [detected];
  if (detected.timestamp.kind !== 'day-row') {
    for (const l of dateTimeLayoutsOf(chosenSheet.rows, shape.dataStartRow, detected.rowFilter)) {
      if (JSON.stringify(l.timestamp) !== JSON.stringify(detected.timestamp)) layouts.push(l);
    }
  }

  const trials: Array<{ layout: Layout; override: ColumnOverride }> = [];
  for (const layout of layouts) {
    const applied = applyLayout(chosenSheet.rows, headerRow, shape.dataStartRow, layout);
    const profiles = profileColumns(applied.rows.slice(applied.dataStartRow), applied.headerRow);
    const timestamps = layout.timestamp.kind === 'date-time'
      ? [layout.timestamp.dateColumn]
      : profiles
        .filter(p => p.stats.nonNullCount > 0 && p.stats.parseableDateCount / p.stats.nonNullCount >= DATE_RATIO)
        .map(p => p.index);
    const values = profiles
      .filter(p => p.stats.nonNullCount > 0 && p.stats.numericCount / p.stats.nonNullCount >= NUMERIC_RATIO)
      .filter(p => p.kind !== 'datetime' && p.kind !== 'id-like' && p.kind !== 'numeric-large')
      .filter(labelled)
      .map(p => p.index);
    for (const timestampIndex of timestamps) {
      for (const demandIndex of values) {
        if (timestampIndex !== demandIndex) trials.push({ layout, override: { timestampIndex, demandIndex } });
      }
    }
  }
  return trials.slice(0, MAX_TRIALS);
}

const peakOf = (r: AMIParseResult) => r.data.reduce((m, p) => Math.max(m, p.kw), -Infinity);
const columnsOf = (r: AMIParseResult) => {
  const t = (r.metadata?.telemetry ?? {}) as { timestamp_column?: string | null; demand_column?: string | null };
  return `${t.timestamp_column ?? '?'} and ${t.demand_column ?? '?'}`;
};

function withFlag(result: AMIParseResult, flag: PlausibilityFlag): AMIParseResult {
  const metadata = result.metadata ?? ({} as NonNullable<AMIParseResult['metadata']>);
  const flags = [...((metadata.plausibilityFlags ?? []) as PlausibilityFlag[]), flag];
  return { ...result, metadata: { ...metadata, plausibilityFlags: flags } };
}

/**
 * Try the other readings and return the result to use. `first` is the reading
 * that did not hold together; it comes back unchanged when no trial does better.
 */
export function searchReadings(
  ctx: SheetContext,
  detected: Layout,
  first: AMIParseResult,
  readSheet: ReadSheet
): AMIParseResult {
  const passing: Array<{ result: AMIParseResult; score: number }> = [];
  const trials = trialsFor(ctx, detected);
  for (const trial of trials) {
    const result = readSheet(ctx, trial.layout, trial.override, 'trial');
    if (!readingHoldsTogether(result)) continue;
    const c = coherenceOf(result.data);
    passing.push({ result, score: c.regularity + c.coverage });
  }
  if (passing.length === 0) return first;
  passing.sort((a, b) => b.score - a.score);

  // A first reading that loaded is kept: the search only flags it. Swapping a
  // loaded reading for another would move a number the preparer may already
  // have seen, and an uneven series can be a real one with outages in it.
  if (first.data.length > 0) {
    const differing = passing.find(p => Math.abs(peakOf(p.result) - peakOf(first)) > 1e-6);
    if (!differing) return first;
    return withFlag(first, {
      check: 'rival_readings',
      severity: 'verify',
      message:
        `This file's readings are unevenly spaced as read from ${columnsOf(first)}. Reading it from ` +
        `${columnsOf(differing.result)} gives an evenly spaced history with a different peak. ` +
        'Check the columns, or choose them yourself.',
      detail: { first: columnsOf(first), alternative: columnsOf(differing.result) },
    });
  }

  // The first reading failed outright: use the best trial, and say so.
  const best = passing[0].result;
  const note =
    `TrueLoad's first reading of this file did not hold together, so it tried the file's other columns and read ` +
    `${columnsOf(best)}, which give an evenly spaced meter history.`;
  let chosen: AMIParseResult = {
    ...best,
    warnings: [note, ...best.warnings],
    metadata: best.metadata && {
      ...best.metadata,
      dataQuality: [note, ...((best.metadata.dataQuality ?? []) as string[])],
      readingSearch: { tried: trials.length, passed: passing.length, firstError: first.errors[0] ?? null },
    },
  };
  const rival = passing.slice(1).find(p => Math.abs(peakOf(p.result) - peakOf(best)) > 1e-6);
  if (rival) {
    chosen = withFlag(chosen, {
      check: 'rival_readings',
      severity: 'verify',
      message:
        `This file can also be read from ${columnsOf(rival.result)}, which gives a different peak. ` +
        'Check that the columns TrueLoad used are the right ones, or choose them yourself.',
      detail: { used: columnsOf(best), alternative: columnsOf(rival.result) },
    });
  }
  return chosen;
}
